import { useEffect, useState } from "react";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Handshake, ShieldCheck, XCircle, Trash2 } from "lucide-react";

const empty = () => ({
  contract_name: "Service Agreement",
  parties: { provider_name: "", provider_email: "", provider_phone: "", client_name: "", client_contact: "", client_email: "", client_phone: "" },
  services: { included: [], scope: "", deliverables: "", not_included: "" },
  arrangement: { service_type: "hourly", hours_included: "", hours_per_week: "", hours_per_month: "", turnaround: "" },
  payment_terms: { billing_method: "", rate: "", billing_frequency: "", estimated_amount: "", payment_due: "", payment_method: "", late_terms: "" },
  confidentiality: { agreed: false, additional: "" },
  signature: { full_name: "", title: "", signature: "", signed_at: "" },
  effective_date: "", end_date: "",
});

export default function ContractPanel({ client, recordId, onSaved, onContext }) {
  const { currentOrgId } = useAuth();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!recordId) {
      setEditing(null); setForm(empty()); setStatus("draft");
      if (client && currentOrgId) {
        supabase.from("organizations").select("name,billing_email").eq("id", currentOrgId).limit(1).maybeSingle().then(({ data: org }) => {
          setForm((f) => ({ ...f, parties: {
            ...f.parties,
            provider_name: org?.name || "", provider_email: org?.billing_email || "",
            client_name: client.referral_source || `${client.first_name} ${client.last_name}`,
            client_contact: `${client.first_name} ${client.last_name}`,
            client_email: client.email || "", client_phone: client.phone || "",
          }}));
        });
      }
      return;
    }
    setLoading(true);
    supabase.from("service_contracts").select("*, clients(first_name,last_name,email,phone)").eq("id", recordId).limit(1).maybeSingle().then(({ data: r }) => {
      if (!r) return;
      const base = empty();
      setEditing(r);
      setForm({
        contract_name: r.contract_name || "Service Agreement",
        parties: { ...base.parties, ...(r.parties || {}) },
        services: { ...base.services, ...(r.services || {}) },
        arrangement: { ...base.arrangement, ...(r.arrangement || {}) },
        payment_terms: { ...base.payment_terms, ...(r.payment_terms || {}) },
        confidentiality: { ...base.confidentiality, ...(r.confidentiality || {}) },
        signature: { ...base.signature, ...(r.signature || {}) },
        effective_date: r.effective_date || "", end_date: r.end_date || "",
      });
      setStatus(r.status || "draft");
    }).finally(() => setLoading(false));
    /* eslint-disable-next-line */
  }, [recordId, client?.id, currentOrgId]);

  const save = async (newStatus) => {
    if (!client) return toast.error("Pick a client first");
    if (newStatus === "signed") {
      if (!form.confidentiality.agreed) return toast.error("Confidentiality acknowledgment required to sign");
      if (!form.signature.full_name?.trim() || !form.signature.signature?.trim()) return toast.error("Signature required");
    }
    const payload = {
      organization_id: currentOrgId, client_id: client.id,
      contract_name: form.contract_name,
      parties: form.parties, services: form.services, arrangement: form.arrangement,
      payment_terms: form.payment_terms, confidentiality: form.confidentiality,
      signature: newStatus === "signed" ? { ...form.signature, signed_at: form.signature.signed_at || new Date().toISOString() } : form.signature,
      effective_date: form.effective_date || null, end_date: form.end_date || null,
      status: newStatus || status,
    };
    try {
      let r;
      if (editing) r = await apiFetch(`/contracts/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else r = await apiFetch(`/contracts`, { method: "POST", body: JSON.stringify(payload) });
      toast.success(newStatus === "signed" ? "Contract signed" : "Contract saved");
      onSaved && onSaved({ kind: "contract", id: r?.id || editing?.id });
    } catch (e) { toast.error(e.message); }
  };

  const terminateContract = async () => {
    if (!editing) return;
    const reason = window.prompt("Enter a termination reason (visible in the audit log):", "Terminated by staff");
    if (!reason?.trim()) return;
    try {
      await apiFetch(`/contracts/${editing.id}/terminate`, { method: "POST", body: JSON.stringify({ reason: reason.trim() }) });
      toast.success("Contract terminated (record preserved)");
      onSaved && onSaved({ kind: "contract", id: editing.id });
    } catch (e) { toast.error(e.message); }
  };
  const removeContract = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete contract "${editing.contract_number || editing.contract_name}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/contracts/${editing.id}`, { method: "DELETE" });
      toast.success("Contract deleted");
      onSaved && onSaved({ kind: "contract", id: editing.id, deleted: true });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    const isSigned = editing?.status === "signed" || editing?.status === "active";
    const isEnded = editing?.status === "expired" || editing?.status === "terminated";
    const canHardDelete = editing && !isSigned; // draft/sent/expired/terminated
    onContext && onContext({
      client, status, editing,
      actions: [
        { key: "draft", label: "Save draft", variant: "outline", testid: "ct-save-draft", onClick: () => save("draft") },
        ...(!isEnded ? [
          { key: "sent", label: "Mark sent", variant: "outline", testid: "ct-send", onClick: () => save("sent") },
          { key: "sign", label: "Sign & activate", variant: "primary", testid: "ct-sign", onClick: () => save("signed") },
        ] : []),
        ...(editing && isSigned ? [
          { key: "terminate", label: "Terminate", icon: XCircle, variant: "outline", testid: "ct-terminate", onClick: terminateContract },
        ] : []),
        ...(canHardDelete ? [
          { key: "delete", label: "Delete", icon: Trash2, variant: "outline", testid: "ct-delete", onClick: removeContract },
        ] : []),
      ],
    });
    /* eslint-disable-next-line */
  }, [form, status, editing]);

  if (loading) return <div className="text-center py-16 text-sm text-[#57534E]">Loading contract…</div>;

  return (
    <div className="space-y-4" data-testid="workspace-contract">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#57534E] flex items-center gap-1.5"><Handshake size={13} /> Contract</div>
        <div className="text-lg font-semibold font-display">{editing?.contract_number || "New Contract"} {client && <span className="text-sm text-[#57534E]">— {client.first_name} {client.last_name}</span>}</div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Contract name"><Input data-testid="ct-name" value={form.contract_name} onChange={(e) => setForm({ ...form, contract_name: e.target.value })} /></Field>
          <Field label="Effective date"><Input type="date" data-testid="ct-effective" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></Field>
          <Field label="End date"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#57534E]">Parties</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Provider name"><Input value={form.parties.provider_name} onChange={(e) => setForm({ ...form, parties: { ...form.parties, provider_name: e.target.value } })} /></Field>
          <Field label="Provider email"><Input value={form.parties.provider_email} onChange={(e) => setForm({ ...form, parties: { ...form.parties, provider_email: e.target.value } })} /></Field>
          <Field label="Client business"><Input value={form.parties.client_name} onChange={(e) => setForm({ ...form, parties: { ...form.parties, client_name: e.target.value } })} /></Field>
          <Field label="Client contact"><Input value={form.parties.client_contact} onChange={(e) => setForm({ ...form, parties: { ...form.parties, client_contact: e.target.value } })} /></Field>
          <Field label="Client email"><Input value={form.parties.client_email} onChange={(e) => setForm({ ...form, parties: { ...form.parties, client_email: e.target.value } })} /></Field>
          <Field label="Client phone"><Input value={form.parties.client_phone} onChange={(e) => setForm({ ...form, parties: { ...form.parties, client_phone: e.target.value } })} /></Field>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#57534E]">Scope of services</div>
        <Field label="Scope"><Textarea rows={3} value={form.services.scope} onChange={(e) => setForm({ ...form, services: { ...form.services, scope: e.target.value } })} /></Field>
        <Field label="Deliverables"><Textarea rows={2} value={form.services.deliverables} onChange={(e) => setForm({ ...form, services: { ...form.services, deliverables: e.target.value } })} /></Field>
        <Field label="Not included"><Textarea rows={2} value={form.services.not_included} onChange={(e) => setForm({ ...form, services: { ...form.services, not_included: e.target.value } })} /></Field>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#57534E]">Payment terms</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Billing method">
            <Select value={form.payment_terms.billing_method || "hourly"} onValueChange={(v) => setForm({ ...form, payment_terms: { ...form.payment_terms, billing_method: v } })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["hourly", "monthly retainer", "project", "milestone"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Rate"><Input placeholder="e.g. $60/hour" value={form.payment_terms.rate} onChange={(e) => setForm({ ...form, payment_terms: { ...form.payment_terms, rate: e.target.value } })} /></Field>
          <Field label="Billing frequency"><Input placeholder="e.g. Bi-weekly" value={form.payment_terms.billing_frequency} onChange={(e) => setForm({ ...form, payment_terms: { ...form.payment_terms, billing_frequency: e.target.value } })} /></Field>
          <Field label="Payment due"><Input placeholder="e.g. Net 14" value={form.payment_terms.payment_due} onChange={(e) => setForm({ ...form, payment_terms: { ...form.payment_terms, payment_due: e.target.value } })} /></Field>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#57534E] flex items-center gap-2"><ShieldCheck size={13} /> Signature</div>
        <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-[#FAF8F5] cursor-pointer">
          <input type="checkbox" checked={!!form.confidentiality.agreed} onChange={(e) => setForm({ ...form, confidentiality: { ...form.confidentiality, agreed: e.target.checked } })} className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="ct-agree" />
          <span className="text-sm">I agree to the confidentiality terms and consent to sign electronically.</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Signer full name *"><Input data-testid="ct-signer-name" value={form.signature.full_name} onChange={(e) => setForm({ ...form, signature: { ...form.signature, full_name: e.target.value } })} /></Field>
          <Field label="Title"><Input value={form.signature.title} onChange={(e) => setForm({ ...form, signature: { ...form.signature, title: e.target.value } })} /></Field>
          <Field label="Type signature *"><Input data-testid="ct-signature" value={form.signature.signature} onChange={(e) => setForm({ ...form, signature: { ...form.signature, signature: e.target.value } })} /></Field>
          <Field label="Date signed"><Input readOnly value={form.signature.signed_at ? new Date(form.signature.signed_at).toLocaleDateString() : new Date().toLocaleDateString()} className="bg-[#FAF8F5]" /></Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-sm">{label}</Label><div className="mt-1">{children}</div></div>; }
